import { defineStore, storeToRefs } from "pinia";
import { useDialog } from "primevue/usedialog";
import { computed, ref } from "vue";

import { useServices } from "@/lib/openapi";
import type { WhatsNewEntryResponse } from "@/lib/openapi/gen/types.gen";

export type { WhatsNewEntryResponse };

export const WHATS_NEW_PREVIEW_LIMIT = 5;

type FetchEntriesOptions = {
    limit?: number;
    offset?: number;
};

export const useWhatsNewStore = defineStore("whats-new", () => {
    const entries = ref<WhatsNewEntryResponse[]>([]);
    const unseenEntries = ref<WhatsNewEntryResponse[]>([]);
    const isLoading = ref(false);
    const error = ref<string>();

    const unseenCount = computed(() => unseenEntries.value.length);
    const hasUnseen = computed(() => unseenCount.value > 0);

    // Dialog requires Vue's inject() context; initialize during component setup().
    let dialog: ReturnType<typeof useDialog> | null = null;
    const initDialog = () => {
        if (!dialog) {
            dialog = useDialog();
        }
    };
    const getDialog = () => {
        if (!dialog) {
            throw new Error(
                "Dialog not initialized. Call whatsNewStore.initDialog() during component setup()."
            );
        }
        return dialog;
    };

    // These endpoints only need auth — no stek/org context required.
    const unauthorizedApi = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: false,
            organization: false,
            stek: false,
        },
    });

    const api = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    const fetchEntries = async (options: FetchEntriesOptions = {}) => {
        isLoading.value = true;
        error.value = undefined;
        try {
            const { response, data } =
                await unauthorizedApi.getEntriesWhatsNewWhatsNewMgtEntriesGet({
                    query: {
                        limit: options.limit,
                        offset: options.offset,
                    },
                });
            if (!response?.ok) {
                error.value = "Failed to load What's New entries.";
                return;
            }
            entries.value = data ?? [];
        } catch (err) {
            console.error("Failed to fetch What's New entries:", err);
            error.value = "Failed to load What's New entries.";
        } finally {
            isLoading.value = false;
        }
    };

    const fetchUnseenEntries = async () => {
        isLoading.value = true;
        error.value = undefined;
        try {
            const { response, data } =
                await api.getUnseenEntriesWhatsNewWhatsNewMgtEntriesUnseenGet(
                    {}
                );
            if (!response?.ok) {
                error.value = "Failed to load unseen What's New entries.";
                return;
            }
            unseenEntries.value = data ?? [];
        } catch (err) {
            console.error("Failed to fetch unseen What's New entries:", err);
            error.value = "Failed to load unseen What's New entries.";
        } finally {
            isLoading.value = false;
        }
    };

    const markSeen = async (entryId: string) => {
        try {
            const { response } =
                await api.markSeenWhatsNewWhatsNewMgtEntriesEntryIdSeenPost({
                    path: { entry_id: entryId },
                });
            if (response?.ok) {
                // Remove from unseen list immediately for responsive UI
                unseenEntries.value = unseenEntries.value.filter(
                    (e) => e.identity !== entryId
                );
            }
        } catch (err) {
            console.error("Failed to mark What's New entry as seen:", err);
        }
    };

    const openModal = (startWithUnseen = true, initialEntryId?: string) => {
        // Lazy import to avoid circular dependency
        import("@/components/dialogs/WhatsNewModal.vue").then((module) => {
            getDialog().open(module.default, {
                props: {
                    header: "What's New",
                    style: { width: "640px", maxWidth: "95vw" },
                    modal: true,
                    dismissableMask: true,
                    closeOnEscape: true,
                },
                data: { initialEntryId, startWithUnseen },
            });
        });
    };

    return {
        entries,
        unseenEntries,
        unseenCount,
        hasUnseen,
        isLoading,
        error,
        fetchEntries,
        fetchUnseenEntries,
        markSeen,
        initDialog,
        openModal,
    };
});

export const useWhatsNewManager = () => storeToRefs(useWhatsNewStore());
